/**
 * Turning a movement refusal into something an operator can act on.
 *
 * The movement endpoints answer a refusal with a bare `reason` code
 * ({ success: false, reason: 'no_predefined_waypoints' }), and the
 * service layer re-throws that code verbatim as the Error message.
 * Shown raw it tells an operator nothing about which control to
 * touch, so every reason we can produce gets a sentence naming the
 * fix.
 *
 * The second job is deciding whether a failure is about identity.
 * Only an identity failure is worth offering "reset the signed
 * session" for: the reset clears the cached bearer token, so the next
 * write re-runs the SEP-10 challenge and asks the operator to sign.
 * Offering it beside a domain refusal — an empty waypoint list, say —
 * sends them signing challenges that cannot possibly help.
 */

const REASON_TEXT: Record<string, string> = {
  // agentMovementService refusals
  no_predefined_waypoints:
    'This loop is set to cycle predefined waypoints but none are saved. Search a place below, press "Save list", then Start — or switch to "Random POIs in country".',
  country_required_for_random_mode:
    'Random and Both modes need a complete two-letter country code, such as AE or US.',
  no_country_set:
    'Set a two-letter country code so random places can be drawn from it.',
  random_place_service_unavailable:
    'The random-place service is not reachable right now. Predefined waypoints still work.',
  invalid_mode:      'That movement mode is not recognised.',
  invalid_waypoints: 'The waypoint list was rejected as malformed.',
  invalid_address:   'This agent address is not a valid Stellar address.',
  not_started:       'This loop is not running yet — press Start first.',

  // Ownership refusals from `_verifyOperatorOwnsAgent`. Signing again cannot
  // change any of these — the wallet is already proven, it just isn't the
  // wallet this agent answers to.
  operator_mismatch:
    'This agent is registered to a different operator wallet, so this wallet cannot control it.',
  agent_not_registered:
    'This agent has no on-chain registry record. Re-register it on the Agents tab, then start the loop.',
  agent_revoked:
    'This agent has been revoked on-chain and cannot be moved.',
  registry_lookup_failed:
    'The on-chain agent registry could not be reached to confirm ownership. Try again in a moment.',

  // Not an operator error at all: the server read an envelope shape it does
  // not know, which is what an SDK upgrade looks like from here.
  signature_unreadable:
    'The server could not read the signature format your wallet produced. This is a server-side incompatibility rather than something re-signing will fix — please report it.',
};

/**
 * Reasons that mean "prove who you are again" — the bearer checks in
 * agentMovementAuthService (`readBearerForAgent`, `_verifyToken`) plus
 * the proof failures from `proveOperator`.
 */
const AUTH_REASONS = new Set([
  'missing_authorization',
  'malformed_authorization',
  'malformed_token',
  'malformed_token_body',
  'unsupported_version',
  'invalid_signature',
  'token_expired',
  'token_agent_mismatch',
  'missing_subjects',
  'missing_challenge_or_signature',
  'operator_signature_mismatch',

  // `proveOperator` hands a failed challenge straight back, so every refusal
  // broadcastAuthService can produce reaches this panel verbatim. Each one
  // means the challenge that was signed is spent, stale, or not the one the
  // server issued — which is exactly what resetting the session repairs.
  'challenge_not_found',
  'challenge_expired',
  'challenge_already_used',
  'challenge_failed',
  'malformed_envelope',
  'source_mismatch',
  'manage_data_op_missing',
  'nonce_mismatch',
  'signature_invalid',
  'invalid_wallet',
  'invalid_operator_address',
  'missing_operator_or_agent',
]);

const AUTH_TEXT =
  'The signed session for this agent is no longer valid. Reset it and sign the challenge again.';

export interface MovementErrorText {
  /** Prose safe to show an operator. */
  text: string;
  /** True only when re-signing a session could plausibly fix it. */
  auth: boolean;
}

/**
 * Describe a thrown movement error. `fallback` handles everything we
 * don't recognise — pass `humanizeUnknownError` from the caller so
 * this module stays free of Soroban decoding concerns.
 */
export function describeMovementError(
  err: unknown,
  fallback: (e: unknown) => string,
): MovementErrorText {
  const raw = (err instanceof Error ? err.message : String(err)).trim();
  const known = REASON_TEXT[raw];
  if (known) return { text: known, auth: false };
  if (AUTH_REASONS.has(raw)) return { text: AUTH_TEXT, auth: true };
  return { text: fallback(err), auth: false };
}

/** Exposed so tests can assert the tables stay in step with the server. */
export const _internal = { REASON_TEXT, AUTH_REASONS, AUTH_TEXT };
